// Task 7.5: GDPR Consent Banner with Cookie Categories

'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { updateConsent } from '@/lib/analytics';
import { captureError, buildTags, toSerializable } from '@/lib/monitoring/logger';
import { fetchWithCSRFToken } from '@/hooks/use-csrf';


interface ConsentState {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  recordings?: boolean;
  timestamp: Date;
}

interface ConsentBannerProps {
  onConsentUpdate?: (consent: ConsentState) => void;
}

export function ConsentBanner({ onConsentUpdate }: ConsentBannerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [consent, setConsent] = useState<ConsentState>({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    recordings: false,
    timestamp: new Date()
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // Check if consent has already been given
    const existingConsent = document.cookie
      .split('; ')
      .find(row => row.startsWith('gdpr-consent='));
    
    if (!existingConsent) {
      setIsVisible(true);
    }

    // Listen for manage consent events
    const handleManageConsent = () => {
      setIsVisible(true);
    };
    
    window.addEventListener('manage-consent', handleManageConsent);
    return () => window.removeEventListener('manage-consent', handleManageConsent);
  }, []);

  const handleConsentChange = (category: keyof ConsentState, value: boolean) => {
    if (category === 'necessary') return; // Cannot disable necessary cookies

    setConsent(prev => ({
      ...prev,
      analytics: category === 'recordings' && value ? true : category === 'analytics' ? value : prev.analytics,
      recordings: category === 'analytics' && !value ? false : category === 'recordings' ? value : prev.recordings,
      [category]: value,
      timestamp: new Date()
    }));
  };

  const logConsentAudit = async (consentToSave: ConsentState) => {
    try {
      const response = await fetchWithCSRFToken('/api/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categories: consentToSave,
          decision: 'granted',
          timestamp: consentToSave.timestamp.toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`Consent API failed with status ${response.status}`);
      }
    } catch (error) {
      void captureError(error, {
        tags: buildTags('consent-banner', 'audit-log-error'),
        extra: toSerializable({ consent: consentToSave })
      });
    }
  };

  const handleAcceptSelected = async (selectedConsent: ConsentState = consent) => {
    setIsLoading(true);

    try {
      updateConsent(selectedConsent);
      onConsentUpdate?.(selectedConsent);
      setErrorMessage(null);
      setIsVisible(false);
      await logConsentAudit(selectedConsent);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptAll = () => {
    const allConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      recordings: true,
      timestamp: new Date()
    };
    
    setConsent(allConsent);
    void handleAcceptSelected(allConsent);
  };

  const handleRejectOptional = () => {
    const minimalConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      recordings: false,
      timestamp: new Date()
    };
    
    setConsent(minimalConsent);
    void handleAcceptSelected(minimalConsent);
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-2 bg-background/60 backdrop-blur supports-[backdrop-filter]:bg-background/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-title"
    >
      <Card className="max-w-2xl mx-auto p-4">
        <h3 id="consent-title" className="text-base font-semibold mb-2">Cookie Preferences</h3>
        
        <p className="text-xs text-muted-foreground mb-4">
          Choose which optional cookies to allow. Necessary cookies are always on for security.
        </p>
        
        <div className="space-y-3 mb-4">
          {/* Necessary Cookies */}
          <div className="flex items-start gap-3">
            <Checkbox 
              checked={true} 
              disabled={true}
              id="necessary"
              aria-label="Necessary cookies (always required)"
            />
            <div className="flex-1">
              <label htmlFor="necessary" className="text-sm font-medium">
                Necessary Cookies
              </label>
              <p className="text-xs text-muted-foreground">
                Essential for authentication, security, and basic functionality. Cannot be disabled.
              </p>
            </div>
          </div>
          
          {/* Analytics Cookies */}
          <div className="flex items-start gap-3">
            <Checkbox 
              checked={consent.analytics}
              onCheckedChange={(checked) => handleConsentChange('analytics', !!checked)}
              id="analytics"
              aria-label="Analytics cookies"
            />
            <div className="flex-1">
              <label htmlFor="analytics" className="text-sm font-medium">
                Analytics Cookies
              </label>
              <p className="text-xs text-muted-foreground">
                Help us understand how you use our service to improve performance and user experience.
              </p>
            </div>
          </div>

          {/* Experience Recordings */}
          <div className="flex items-start gap-3">
            <Checkbox
              checked={consent.recordings === true}
              onCheckedChange={(checked) => handleConsentChange('recordings', !!checked)}
              id="recordings"
              aria-label="Experience recordings"
            />
            <div className="flex-1">
              <label htmlFor="recordings" className="text-sm font-medium">
                Experience Recordings
              </label>
              <p className="text-xs text-muted-foreground">
                Help us improve public pages by recording interactions after analytics cookies are enabled.
              </p>
            </div>
          </div>
          
          {/* Marketing Cookies */}
          <div className="flex items-start gap-3">
            <Checkbox 
              checked={consent.marketing}
              onCheckedChange={(checked) => handleConsentChange('marketing', !!checked)}
              id="marketing"
              aria-label="Marketing cookies"
            />
            <div className="flex-1">
              <label htmlFor="marketing" className="text-sm font-medium">
                Marketing Cookies
              </label>
              <p className="text-xs text-muted-foreground">
                Used to deliver relevant advertisements and track campaign effectiveness.
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          <Button 
            onClick={handleAcceptAll}
            disabled={isLoading}
            size="sm"
            className="flex-1"
          >
            Accept All Cookies
          </Button>
          
          <Button 
            onClick={() => void handleAcceptSelected()}
            disabled={isLoading}
            variant="outline"
            size="sm"
            className="flex-1"
          >
            {isLoading ? 'Saving...' : 'Accept Selected'}
          </Button>
          
          <Button 
            onClick={handleRejectOptional}
            disabled={isLoading}
            variant="ghost"
            size="sm"
            className="flex-1"
          >
            Reject Optional
          </Button>
        </div>

        {errorMessage && (
          <p className="mt-4 text-sm text-destructive" role="alert">
            {errorMessage}
          </p>
        )}

        <p className="text-xs text-muted-foreground mt-3 text-center">
          Learn more in our{' '}
          <Link href="/privacy" className="underline hover:text-primary">
            Privacy Policy
          </Link>
        </p>
      </Card>
    </div>
  );
}
